import { type FormEvent, useEffect, useRef, useState } from "react";
import { ChevronDownIcon, MinusIcon, PlusIcon } from "../../components/icons";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  useCaseStatuses,
  usePartRequestStatuses,
  useSaveCaseStatuses,
  useSavePartRequestStatuses,
} from "../../lib/queries";

type Row = { key: string; id?: string; label: string; closed: boolean; arrived: boolean; notice: string };

const TEXT = {
  cases: {
    title: "Estados de los casos",
    intro:
      "En este orden avanza un caso. El mensaje se sugiere al asesor cuando un caso llega a ese estado; puede usar",
    vars: ["{nombre}", "{caso}", "{producto}"],
    closes: "Cierra el caso",
    inUse: "Hay casos en",
    move: "pásalos",
    notice: "Mensaje sugerido (opcional). Ej.: ¡Buenas noticias, {nombre}! El caso {caso} ya está listo para retirar.",
  },
  parts: {
    title: "Estados de los repuestos",
    intro:
      "En este orden avanza una solicitud de repuesto. El mensaje se sugiere al asesor cuando llega a ese estado; puede usar",
    vars: ["{nombre}", "{solicitud}", "{repuesto}"],
    closes: "Cierra la solicitud",
    inUse: "Hay solicitudes en",
    move: "pásalas",
    notice: "Mensaje sugerido (opcional). Ej.: ¡Buenas noticias, {nombre}! Ya tenemos {repuesto}.",
  },
} as const;

/**
 * The company's statuses for cases or part requests, in order: rename, reorder, add or
 * remove them, mark which ones close it (and, for parts, the one where the part arrives)
 * and write the message suggested when one gets there.
 */
export function StatusesDialog({ flow = "cases", onClose }: { flow?: "cases" | "parts"; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const caseStatuses = useCaseStatuses().data;
  const partStatuses = usePartRequestStatuses().data;
  const saveCases = useSaveCaseStatuses();
  const saveParts = useSavePartRequestStatuses();
  const parts = flow === "parts";
  const statuses = parts ? partStatuses : caseStatuses?.map((s) => ({ ...s, arrived: false }));
  const save = parts ? saveParts : saveCases;
  const t = TEXT[flow];
  const toast = useToast();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  useEffect(() => {
    if (statuses && !rows)
      setRows(
        statuses.map((s) => ({
          key: s.id,
          id: s.id,
          label: s.label,
          closed: s.closed,
          arrived: s.arrived,
          notice: s.notice ?? "",
        })),
      );
  }, [statuses, rows]);

  const update = (i: number, patch: Partial<Row>) =>
    setRows((r) => r?.map((row, j) => (j === i ? { ...row, ...patch } : row)) ?? null);
  const move = (i: number, by: -1 | 1) =>
    setRows((r) => {
      if (!r || i + by < 0 || i + by >= r.length) return r;
      const next = [...r];
      const [row] = next.splice(i, 1);
      if (row) next.splice(i + by, 0, row);
      return next;
    });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!rows) return;
    setError(null);
    try {
      await save.mutateAsync(
        rows.map((r) => ({
          id: r.id,
          label: r.label.trim(),
          closed: r.closed,
          arrived: r.arrived && !r.closed,
          notice: r.notice.trim() || null,
        })),
      );
      toast("Estados guardados");
      dialog.current?.close();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "";
      setError(
        msg.startsWith("In use: ")
          ? `${t.inUse} ${msg.slice(8)}: ${t.move} a otro estado antes de quitarlo.`
          : msg.includes("open and one closed")
            ? "Hace falta al menos un estado abierto y uno que cierre."
            : msg.includes("same name")
              ? "Dos estados tienen el mismo nombre."
              : "No se pudieron guardar los estados.",
      );
    }
  }

  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="statuses-title" className="statuses-dialog">
      <form className="dialog-form" onSubmit={onSubmit}>
        <div>
          <h3 id="statuses-title">{t.title}</h3>
          <p className="muted">
            {t.intro} <span className="mono">{t.vars[0]}</span>, <span className="mono">{t.vars[1]}</span> y{" "}
            <span className="mono">{t.vars[2]}</span>.
            {parts && " Marca en qué estado ya llegó el repuesto: ahí Orbis ofrece avanzar el caso que lo espera."}
          </p>
        </div>
        <ol className="status-rows">
          {rows?.map((r, i) => (
            <li key={r.key} className="status-row">
              <div className="status-row-head">
                <input
                  className="input"
                  aria-label={`Nombre del estado ${i + 1}`}
                  placeholder="Ej.: En revisión"
                  required
                  maxLength={40}
                  value={r.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                />
                <label className="check">
                  <input type="checkbox" checked={r.closed} onChange={(e) => update(i, { closed: e.target.checked })} />
                  {t.closes}
                </label>
                {parts && (
                  <label className="check">
                    <input
                      type="radio"
                      name="arrived"
                      checked={r.arrived}
                      disabled={r.closed}
                      onChange={() => setRows((all) => all?.map((x, j) => ({ ...x, arrived: j === i })) ?? null)}
                    />
                    Llegó el repuesto
                  </label>
                )}
                <span className="status-row-tools">
                  <button
                    className="btn btn-sm btn-ghost up"
                    type="button"
                    aria-label={`Subir ${r.label}`}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ChevronDownIcon />
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    type="button"
                    aria-label={`Bajar ${r.label}`}
                    disabled={i === rows.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ChevronDownIcon />
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    type="button"
                    aria-label={`Quitar ${r.label}`}
                    disabled={rows.length <= 2}
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  >
                    <MinusIcon />
                  </button>
                </span>
              </div>
              <textarea
                className="textarea"
                rows={2}
                aria-label={`Mensaje sugerido para ${r.label}`}
                placeholder={t.notice}
                maxLength={600}
                value={r.notice}
                onChange={(e) => update(i, { notice: e.target.value })}
              />
            </li>
          ))}
        </ol>
        <button
          className="btn btn-sm status-add"
          type="button"
          disabled={!rows || rows.length >= 20}
          onClick={() =>
            setRows((r) => [
              ...(r ?? []),
              { key: crypto.randomUUID(), label: "", closed: false, arrived: false, notice: "" },
            ])
          }
        >
          <PlusIcon />
          Agregar estado
        </button>
        {error && <div className="banner banner-warn">{error}</div>}
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="submit" disabled={save.isPending || !rows}>
            {save.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
